import { useEffect, useState } from 'react';
import { ConnectionState, RoomEvent, type Room } from 'livekit-client';

export interface ConnectionStatus {
  state: ConnectionState;
  /** True between a network drop and the session being restored. */
  reconnecting: boolean;
  /** Incremented on every successful reconnect, for a transient "back online" toast. */
  reconnectCount: number;
}

/**
 * Surfaces the reconnect lifecycle so the UI can cover the call with an
 * overlay instead of showing frozen tiles. LiveKit performs an ICE restart on
 * a 4G-to-Wi-Fi switch and only gives up after roughly a minute, so the honest
 * message during that window is "reconnecting", not "call ended".
 */
export function useConnectionState(room: Room | undefined): ConnectionStatus {
  const [state, setState] = useState<ConnectionState>(ConnectionState.Disconnected);
  const [reconnectCount, setCount] = useState(0);

  useEffect(() => {
    if (!room) return;

    setState(room.state);
    const onState = (next: ConnectionState) => setState(next);
    const onReconnected = () => setCount((n) => n + 1);

    room.on(RoomEvent.ConnectionStateChanged, onState);
    room.on(RoomEvent.Reconnected, onReconnected);

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, onState);
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room]);

  return {
    state,
    reconnecting: state === ConnectionState.Reconnecting || state === ConnectionState.SignalReconnecting,
    reconnectCount,
  };
}
